import React from 'react';
import { Helmet } from 'react-helmet-async';
import { City } from '@/data/cities';
import { CityLanguage } from '@/context/languages/languageData';
import { detectCityLanguage } from '@/utils/cityLanguageDetector';
import { useCityTranslations } from '@/hooks/useCityTranslations';

interface CityLandingPageSEOProps {
  city: City;
  language?: CityLanguage;
}

const CityLandingPageSEO: React.FC<CityLandingPageSEOProps> = ({ 
  city, 
  language = 'en' 
}) => {
  const { translations } = useCityTranslations(city, language);
  const cleanCityName = city.name.replace(/\s+\d+$/, '').trim();
  const baseUrl = 'https://psychotherapist.help';
  
  // Detect local language for this city
  const localLanguage = detectCityLanguage(city);
  
  // Generate bilingual alternate URLs (only English + local language)
  const generateBilingualAlternateUrls = () => {
    const alternates = [];
    const languages: CityLanguage[] = localLanguage === 'en' ? ['en'] : ['en', localLanguage];
    
    languages.forEach(lang => {
      const url = lang === 'en' 
        ? `${baseUrl}/city/${city.slug}`
        : `${baseUrl}/${lang}/city/${city.slug}`;
      alternates.push({ lang, url });
    });
    
    // Add x-default pointing to English version
    alternates.push({ 
      lang: 'x-default', 
      url: `${baseUrl}/city/${city.slug}` 
    });
    
    return alternates;
  };

  const alternateUrls = generateBilingualAlternateUrls();
  const currentUrl = language === 'en' 
    ? `${baseUrl}/city/${city.slug}`
    : `${baseUrl}/${language}/city/${city.slug}`;

  const title = translations.metaTitle || translations.heroTitle || `Expert Psychotherapists in ${cleanCityName}`;
  const description = translations.metaDescription || translations.heroDescription || `Find qualified psychotherapists in ${cleanCityName}, ${city.country}. Expert mental health professionals offering therapy, counseling, and psychological services.`;
  const keywords = translations.metaKeywords || `psychotherapists, ${cleanCityName}, mental health, therapy, psychology, counseling, ${city.country}`;

  // FAQ Schema.org data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": translations.faq1Q || `How much does therapy cost in ${cleanCityName}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": translations.faq1A || `Therapy costs in ${cleanCityName} typically range from $50 to $200 per session, depending on the therapist's experience and specialization.`
        }
      },
      {
        "@type": "Question",
        "name": translations.faq2Q || `Is online therapy available in ${cleanCityName}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": translations.faq2A || `Yes, many therapists in ${cleanCityName} offer secure online therapy sessions via video call, providing flexible access to mental health care.`
        }
      },
      {
        "@type": "Question",
        "name": translations.faq3Q || `How do I choose the right therapist in ${cleanCityName}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": translations.faq3A || `Consider factors like specialization, treatment approach, availability, and whether you prefer in-person or online sessions. Read reviews and book a consultation to find the best fit.`
        }
      }
    ]
  };

  // Breadcrumb Schema.org data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": translations.breadcrumbHome || "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": translations.breadcrumbCountry || city.country,
        "item": `${baseUrl}/country/${city.countryCode.toLowerCase()}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": translations.breadcrumbCity || cleanCityName
      }
    ]
  };

  // Service Schema.org data
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": `Psychotherapists in ${cleanCityName}`,
    "description": description,
    "serviceType": [
      "Anxiety Treatment",
      "Depression Therapy", 
      "Couples Counseling",
      "Child Psychology",
      "Addiction Recovery",
      "Trauma Therapy",
      "Online Therapy",
      "In-Person Therapy"
    ],
    "areaServed": {
      "@type": "City",
      "name": cleanCityName,
      "addressCountry": city.country
    },
    "availableLanguage": [language, ...(localLanguage !== 'en' ? [localLanguage] : [])],
    "url": currentUrl
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Hreflang Tags - Only English + Local Language */}
      {alternateUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={language === 'en' ? 'en_US' : `${language}_${language.toUpperCase()}`} />
      <meta property="og:site_name" content="Global Therapy Hub" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="author" content="Global Therapy Hub" />
      <meta name="language" content={language} />
      <meta name="geo.region" content={city.countryCode} />
      <meta name="geo.placename" content={`${cleanCityName}, ${city.country}`} />
      {city.coordinates && (
        <>
          <meta name="geo.position" content={`${city.coordinates.lat};${city.coordinates.lng}`} />
          <meta property="place:location:latitude" content={String(city.coordinates.lat)} />
          <meta property="place:location:longitude" content={String(city.coordinates.lng)} />
        </>
      )}
      <meta property="og:image" content={`${baseUrl}/og-image.png`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:image" content={`${baseUrl}/og-image.png`} />
      
      {/* Structured Data: Medical Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalOrganization",
          "name": `Psychotherapists in ${cleanCityName}`,
          "description": description,
          "url": currentUrl,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": cleanCityName,
            "addressCountry": city.country
          },
          "areaServed": {
            "@type": "City",
            "name": cleanCityName,
            "addressCountry": city.country
          },
          "medicalSpecialty": "Psychology",
          "serviceType": "Mental Health Services",
          "availableLanguage": [language, ...(localLanguage !== 'en' ? [localLanguage] : [])],
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${baseUrl}/find?location=${cleanCityName}`,
            "query-input": "required name=search_term_string"
          }
        })}
      </script>

      {/* Structured Data: FAQPage */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* Structured Data: BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      {/* Structured Data: Service */}
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
    </Helmet>
  );
};

export default CityLandingPageSEO;
